import { intlLocaleFor } from "../i18n/locales";
import { useState, useEffect, useCallback } from "react";
import {
  Loader2, Users, UserPlus, Receipt, Crown, Mail, Copy, Check, RotateCw, X,
  Ban, Play, Trash2, Settings2, ArrowLeftRight,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { api, apiError } from "../lib/api";
import WorkspaceJoin from "./WorkspaceJoin";
import i18n from "../i18n";

// Traduction (Mon espace) : les textes suivent la langue active ; la page parente s'abonne aux changements via useTranslation.
const tr = (k, o) => i18n.t(k, o);

const ROLE_LABEL = { get owner() { return tr("workspace:panel.proprietaire"); }, admin: "Administrateur", get member() { return tr("workspace:panel.utilisateur"); }, get billing_admin() { return tr("workspace:panel.respFacturation"); } };
const STATUS_BADGE = {
  active: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  suspended: "bg-red-500/15 text-red-600 border-red-500/30",
  pending: "bg-amber-500/15 text-amber-600 border-amber-500/30",
  expired: "bg-slate-500/15 text-slate-500 border-slate-500/30",
  accepted: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  cancelled: "bg-slate-500/15 text-slate-500 border-slate-500/30",
};
const STATUS_LABEL = { get active() { return tr("workspace:panel.actif"); }, suspended: "Suspendu", get pending() { return tr("workspace:panel.enAttente"); }, get expired() { return tr("workspace:panel.expiree"); }, get accepted() { return tr("workspace:panel.acceptee"); }, get cancelled() { return tr("workspace:panel.annulee"); } };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(intlLocaleFor(i18n.language)) : "—");
const titleLabel = (t) => ({ mr: "M.", mrs: "Mme", none: "" }[t] || "");

export const WorkspacePanel = ({ workspaceId, role, onJoinRequestsChange }) => {
  const canManage = role === "owner" || role === "admin";
  const canBilling = role === "owner" || role === "admin" || role === "billing_admin";
  const canTransfer = role === "owner";

  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [billing, setBilling] = useState({ members: [], invitations: [] });
  const [seats, setSeats] = useState(null);

  // dialogs
  const [inviteOpen, setInviteOpen] = useState(false);
  const [baOpen, setBaOpen] = useState(false);
  const [seatsOpen, setSeatsOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const reqs = [
        api.get(`/workspaces/${workspaceId}/members`),
        api.get(`/workspaces/${workspaceId}/seats`),
      ];
      if (canManage) {
        reqs.push(api.get(`/workspaces/${workspaceId}/invitations`));
        reqs.push(api.get(`/workspaces/${workspaceId}/billing-admins`));
      }
      const res = await Promise.all(reqs);
      setMembers(res[0].data);
      setSeats(res[1].data);
      if (canManage) {
        setInvitations(res[2].data.filter((i) => i.role !== "billing_admin"));
        setBilling(res[3].data);
      }
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [workspaceId, canManage]);

  useEffect(() => { load(); }, [load]);

  const teamMembers = members.filter((m) => m.role !== "billing_admin");

  if (loading || !seats) {
    return <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="workspace-panel">
      {/* Compteur de sièges (§20) */}
      <div className="glass rounded-2xl p-6" data-testid="seat-counter">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-600" />
            <h3 className="font-heading text-lg font-semibold">{tr("workspace:panel.sieges")}</h3>
          </div>
          {canBilling && (
            <Button data-testid="manage-seats-btn" onClick={() => setSeatsOpen(true)} variant="outline"
              className="border-slate-200 bg-slate-100 hover:bg-slate-200 text-slate-900">
              <Settings2 className="w-4 h-4 mr-2" /> {tr("workspace:panel.gererLesSieges")}
            </Button>
          )}
        </div>
        <p className="text-sm text-slate-600 mt-3" data-testid="seat-summary">
          <span className="font-semibold text-slate-900">{seats.active_members} actifs</span>
          {seats.pending_invitations > 0 && <> + {seats.pending_invitations} invitation(s)</>}
          {" "}/ <span className="font-semibold text-slate-900">{seats.purchased_seats} {tr("workspace:panel.sieges2")}</span>
          {" · "}<span className="text-emerald-600">{seats.available_seats} disponible(s)</span>
          {seats.billing_admins > 0 && <span className="text-slate-400"> · {seats.billing_admins} {tr("workspace:panel.respFacturationHorsSieges")}</span>}
        </p>
        {seats.scheduled_seat_count != null && (
          <div className="mt-2 flex items-center gap-2">
            <Badge variant="outline" className="bg-cyan-500/10 text-cyan-600 border-cyan-500/30" data-testid="scheduled-seats-badge">
              {tr("workspace:panel.reductionPlanifiee")} {seats.scheduled_seat_count} {tr("workspace:panel.sieges2")}
            </Badge>
            {canBilling && (
              <button data-testid="cancel-scheduled-seats-btn" onClick={async () => {
                try { await api.post(`/workspaces/${workspaceId}/seats/cancel-scheduled`); toast.success(tr("workspace:panel.reductionAnnulee")); load(); }
                catch (e) { toast.error(apiError(e.response?.data?.detail)); }
              }} className="text-xs text-slate-400 hover:text-slate-600 underline">{tr("workspace:panel.annuler")}</button>
            )}
          </div>
        )}
      </div>

      {/* Équipe (§25) */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-600" />
            <h3 className="font-heading text-lg font-semibold">{tr("workspace:panel.equipe")}</h3>
            <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200" data-testid="member-count">{teamMembers.length}</Badge>
          </div>
          <div className="flex items-center gap-2">
            {canTransfer && teamMembers.some((m) => m.role === "admin" && m.status === "active") && (
              <Button data-testid="transfer-ownership-btn" onClick={() => setTransferOpen(true)} variant="outline"
                className="border-slate-200 bg-white hover:bg-slate-100 text-slate-900">
                <ArrowLeftRight className="w-4 h-4 mr-2" /> {tr("workspace:panel.transfererLaPropriete")}
              </Button>
            )}
            {canManage && (
              <Button data-testid="invite-member-btn" onClick={() => setInviteOpen(true)}
                className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
                <UserPlus className="w-4 h-4 mr-2" /> {tr("workspace:panel.inviterUnMembre")}
              </Button>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table data-testid="team-table">
            <TableHeader>
              <TableRow>
                <TableHead>{tr("workspace:panel.membre")}</TableHead>
                <TableHead>{tr("workspace:panel.eMail")}</TableHead>
                <TableHead>{tr("workspace:panel.role")}</TableHead>
                <TableHead>{tr("workspace:panel.statut")}</TableHead>
                <TableHead>{tr("workspace:panel.arrivee")}</TableHead>
                <TableHead>{tr("workspace:panel.derniereConnexion")}</TableHead>
                {canManage && <TableHead className="text-right">{tr("workspace:panel.actions")}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {teamMembers.map((m) => (
                <TableRow key={m.user_id} data-testid={`member-row-${m.user_id}`}>
                  <TableCell className="font-medium">
                    {titleLabel(m.title)} {m.first_name || ""} {m.last_name || ""}
                    {!m.first_name && !m.last_name && (m.name || "—")}
                  </TableCell>
                  <TableCell className="text-slate-500">{m.email}</TableCell>
                  <TableCell>
                    {canManage && m.role !== "owner" ? (
                      <Select value={m.role} onValueChange={async (newRole) => {
                        if (newRole === m.role) return;
                        try { await api.patch(`/workspaces/${workspaceId}/members/${m.user_id}`, { role: newRole }); toast.success(tr("workspace:panel.roleMisAJour")); load(); }
                        catch (e) { toast.error(apiError(e.response?.data?.detail)); }
                      }}>
                        <SelectTrigger className="w-[150px] h-8" data-testid={`role-select-${m.user_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">{tr("workspace:panel.administrateur")}</SelectItem>
                          <SelectItem value="member">{tr("workspace:panel.utilisateur")}</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200">{ROLE_LABEL[m.role]}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={STATUS_BADGE[m.status] || ""} data-testid={`member-status-${m.user_id}`}>
                      {STATUS_LABEL[m.status] || m.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-slate-500">{fmtDate(m.joined_at)}</TableCell>
                  <TableCell className="text-slate-500">{fmtDate(m.last_login_at)}</TableCell>
                  {canManage && (
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {m.user_id !== undefined && m.role !== "owner" && (
                          m.status === "suspended" ? (
                            <Button size="sm" variant="ghost" data-testid={`reactivate-member-${m.user_id}`}
                              onClick={async () => { try { await api.post(`/workspaces/${workspaceId}/members/${m.user_id}/reactivate`); toast.success(tr("workspace:panel.membreReactive")); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } }}
                              className="text-emerald-600"><Play className="w-4 h-4" /></Button>
                          ) : (
                            <Button size="sm" variant="ghost" data-testid={`suspend-member-${m.user_id}`}
                              onClick={async () => { try { await api.post(`/workspaces/${workspaceId}/members/${m.user_id}/suspend`, {}); toast.success(tr("workspace:panel.membreSuspendu")); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } }}
                              className="text-amber-600"><Ban className="w-4 h-4" /></Button>
                          )
                        )}
                        {m.role !== "owner" && (
                          <Button size="sm" variant="ghost" data-testid={`remove-member-${m.user_id}`}
                            onClick={async () => { if (!window.confirm(tr("workspace:panel.retirerCeMembre"))) return; try { await api.delete(`/workspaces/${workspaceId}/members/${m.user_id}`); toast.success(tr("workspace:panel.membreRetire")); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } }}
                            className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Invitations (§24) */}
      {canManage && (
        <div className="glass rounded-2xl p-6" data-testid="invitations-section">
          <h3 className="font-heading text-lg font-semibold mb-4">{tr("workspace:panel.invitations")}</h3>
          {invitations.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">{tr("workspace:panel.aucuneInvitationEnAttente")}</p>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="invitations-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{tr("workspace:panel.eMail")}</TableHead><TableHead>{tr("workspace:panel.role")}</TableHead><TableHead>{tr("workspace:panel.statut")}</TableHead>
                    <TableHead>{tr("workspace:panel.expire")}</TableHead><TableHead className="text-right">{tr("workspace:panel.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invitations.map((inv) => (
                    <TableRow key={inv.id} data-testid={`invitation-row-${inv.id}`}>
                      <TableCell className="font-medium">{inv.email}</TableCell>
                      <TableCell>{ROLE_LABEL[inv.role]}</TableCell>
                      <TableCell><Badge variant="outline" className={STATUS_BADGE[inv.status] || ""}>{STATUS_LABEL[inv.status] || inv.status}</Badge></TableCell>
                      <TableCell className="text-slate-500">{fmtDate(inv.expires_at)}</TableCell>
                      <TableCell className="text-right">
                        {inv.status === "pending" && (
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="ghost" data-testid={`resend-invitation-${inv.id}`}
                              onClick={async () => { try { await api.post(`/workspaces/${workspaceId}/invitations/${inv.id}/resend`); toast.success(tr("workspace:panel.invitationRenvoyee")); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } }}
                              className="text-slate-600"><RotateCw className="w-4 h-4" /></Button>
                            <Button size="sm" variant="ghost" data-testid={`cancel-invitation-${inv.id}`}
                              onClick={async () => { try { await api.post(`/workspaces/${workspaceId}/invitations/${inv.id}/cancel`); toast.success(tr("workspace:panel.invitationAnnulee")); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } }}
                              className="text-red-600"><X className="w-4 h-4" /></Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}

      {/* Responsables facturation (§30) */}
      {canManage && (
        <div className="glass rounded-2xl p-6" data-testid="billing-admins-section">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-cyan-600" />
              <h3 className="font-heading text-lg font-semibold">{tr("workspace:panel.responsablesFacturation")}</h3>
            </div>
            <Button data-testid="invite-billing-admin-btn" onClick={() => setBaOpen(true)} variant="outline"
              className="border-slate-200 bg-slate-100 hover:bg-slate-200 text-slate-900">
              <UserPlus className="w-4 h-4 mr-2" /> {tr("workspace:panel.inviter")}
            </Button>
          </div>
          <p className="text-xs text-slate-400 mb-3">{tr("workspace:panel.lesResponsablesFacturationNe")}</p>
          {billing.members.length === 0 && billing.invitations.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">{tr("workspace:panel.aucunResponsableFacturation")}</p>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="billing-admins-table">
                <TableHeader>
                  <TableRow><TableHead>{tr("workspace:panel.nomEMail")}</TableHead><TableHead>{tr("workspace:panel.statut")}</TableHead><TableHead>{tr("workspace:panel.derniereConnexion")}</TableHead><TableHead className="text-right">{tr("workspace:panel.actions")}</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {billing.members.map((m) => (
                    <TableRow key={m.user_id} data-testid={`billing-admin-row-${m.user_id}`}>
                      <TableCell className="font-medium">{m.email}</TableCell>
                      <TableCell><Badge variant="outline" className={STATUS_BADGE.active}>{tr("workspace:panel.actif")}</Badge></TableCell>
                      <TableCell className="text-slate-500">{fmtDate(m.last_login_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" data-testid={`remove-billing-admin-${m.user_id}`}
                          onClick={async () => { if (!window.confirm(tr("workspace:panel.retirerCetAccesFacturation"))) return; try { await api.delete(`/workspaces/${workspaceId}/billing-admins/${m.user_id}`); toast.success(tr("workspace:panel.accesRetire")); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } }}
                          className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {billing.invitations.map((inv) => (
                    <TableRow key={inv.id} data-testid={`billing-admin-inv-${inv.id}`}>
                      <TableCell className="font-medium">{inv.email}</TableCell>
                      <TableCell><Badge variant="outline" className={STATUS_BADGE[inv.status] || ""}>{STATUS_LABEL[inv.status] || inv.status}</Badge></TableCell>
                      <TableCell className="text-slate-400">—</TableCell>
                      <TableCell className="text-right">
                        {inv.status === "pending" && (
                          <Button size="sm" variant="ghost" data-testid={`resend-billing-admin-${inv.id}`}
                            onClick={async () => { try { await api.post(`/workspaces/${workspaceId}/invitations/${inv.id}/resend`); toast.success(tr("workspace:panel.invitationRenvoyee")); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } }}
                            className="text-slate-600"><RotateCw className="w-4 h-4" /></Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}

      <InviteMemberDialog open={inviteOpen} onOpenChange={setInviteOpen} workspaceId={workspaceId} onDone={load} />
      <InviteBillingAdminDialog open={baOpen} onOpenChange={setBaOpen} workspaceId={workspaceId} onDone={load} />
      <SeatsDialog open={seatsOpen} onOpenChange={setSeatsOpen} workspaceId={workspaceId} seats={seats} onDone={load} />
      <TransferDialog open={transferOpen} onOpenChange={setTransferOpen} workspaceId={workspaceId}
        admins={teamMembers.filter((m) => m.role === "admin" && m.status === "active")} onDone={load} />

      {/* Demandes d'adhésion & rejoindre un espace */}
      <WorkspaceJoin onDecision={onJoinRequestsChange} />
    </div>
  );
};

const InviteMemberDialog = ({ open, onOpenChange, workspaceId, onDone }) => {
  const [email, setEmail] = useState(""); const [role, setRole] = useState("member");
  const [busy, setBusy] = useState(false); const [link, setLink] = useState("");
  const submit = async () => {
    if (!email.trim()) { toast.error(tr("workspace:panel.renseignezUneAdresseE")); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${workspaceId}/invitations`, { email: email.trim(), role });
      if (data.invite_link) setLink(data.invite_link);
      else { toast.success(tr("workspace:panel.invitationEnvoyee")); onOpenChange(false); setEmail(""); }
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setEmail(""); setLink(""); setRole("member"); } }}>
      <DialogContent data-testid="invite-dialog">
        <DialogHeader><DialogTitle>{tr("workspace:panel.inviterUnMembre")}</DialogTitle>
          <DialogDescription>{tr("workspace:panel.unSiegeEstReserve")}</DialogDescription></DialogHeader>
        {link ? (
          <div className="space-y-3">
            <Label>{tr("workspace:panel.lienDInvitation")}</Label>
            <div className="flex gap-2">
              <Input data-testid="invite-link-input" value={link} readOnly className="bg-slate-50 border-slate-200" />
              <Button data-testid="copy-invite-link-btn" onClick={() => { navigator.clipboard.writeText(link); toast.success(tr("workspace:panel.lienCopie")); }} variant="outline"><Copy className="w-4 h-4" /></Button>
            </div>
            <Button data-testid="invite-done-btn" onClick={() => onOpenChange(false)} className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"><Check className="w-4 h-4 mr-2" />{tr("workspace:panel.termine")}</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div><Label className="mb-1.5 block">{tr("workspace:panel.eMail")}</Label>
              <Input data-testid="invite-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={tr("workspace:panel.collegueEntrepriseNc")} className="bg-slate-50 border-slate-200" /></div>
            <div><Label className="mb-1.5 block">{tr("workspace:panel.role")}</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="invite-role-select" className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="member">{tr("workspace:panel.utilisateur")}</SelectItem><SelectItem value="admin">{tr("workspace:panel.administrateur")}</SelectItem></SelectContent>
              </Select></div>
            <Button data-testid="create-invite-btn" onClick={submit} disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}{tr("workspace:panel.envoyerLInvitation")}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const InviteBillingAdminDialog = ({ open, onOpenChange, workspaceId, onDone }) => {
  const [email, setEmail] = useState(""); const [busy, setBusy] = useState(false); const [link, setLink] = useState("");
  const submit = async () => {
    if (!email.trim()) { toast.error(tr("workspace:panel.renseignezUneAdresseE")); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${workspaceId}/billing-admins`, { email: email.trim() });
      if (data.invite_link) setLink(data.invite_link);
      else { toast.success(tr("workspace:panel.invitationEnvoyee")); onOpenChange(false); setEmail(""); }
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setEmail(""); setLink(""); } }}>
      <DialogContent data-testid="billing-admin-dialog">
        <DialogHeader><DialogTitle>{tr("workspace:panel.inviterUnResponsableFacturation")}</DialogTitle>
          <DialogDescription>{tr("workspace:panel.accesFinancierUniquementNe")}</DialogDescription></DialogHeader>
        {link ? (
          <div className="space-y-3">
            <Label>{tr("workspace:panel.lienDInvitation")}</Label>
            <div className="flex gap-2">
              <Input data-testid="ba-invite-link-input" value={link} readOnly className="bg-slate-50 border-slate-200" />
              <Button onClick={() => { navigator.clipboard.writeText(link); toast.success(tr("workspace:panel.lienCopie")); }} variant="outline"><Copy className="w-4 h-4" /></Button>
            </div>
            <Button onClick={() => onOpenChange(false)} className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">{tr("workspace:panel.termine")}</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div><Label className="mb-1.5 block">{tr("workspace:panel.eMail")}</Label>
              <Input data-testid="billing-admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={tr("workspace:panel.comptaEntrepriseNc")} className="bg-slate-50 border-slate-200" /></div>
            <Button data-testid="create-billing-admin-btn" onClick={submit} disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}{tr("workspace:panel.envoyerLInvitation")}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const SeatsDialog = ({ open, onOpenChange, workspaceId, seats, onDone }) => {
  const [value, setValue] = useState(seats?.purchased_seats || 1);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setValue(seats?.purchased_seats || 1); }, [open, seats]);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/workspaces/${workspaceId}/seats`, { seats: Number(value) });
      if (data.scheduled_seat_count != null && Number(value) < seats.purchased_seats) toast.success(tr("workspace:panel.reductionPlanifieeALa"));
      else toast.success(tr("workspace:panel.siegesMisAJour"));
      onOpenChange(false); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const isReduction = Number(value) < (seats?.purchased_seats || 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="seats-dialog">
        <DialogHeader><DialogTitle>{tr("workspace:panel.gererLesSieges")}</DialogTitle>
          <DialogDescription>{tr("workspace:panel.augmentationImmediateReductionPlanifiee")}</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div><Label className="mb-1.5 block">{tr("workspace:panel.nombreDeSieges")}</Label>
            <Input data-testid="seats-input" type="number" min={1} value={value} onChange={(e) => setValue(e.target.value)} className="bg-slate-50 border-slate-200" /></div>
          <p className="text-xs text-slate-500">
            {tr("workspace:panel.actuel")} {seats?.purchased_seats} {tr("workspace:panel.utilises")} {seats?.used_seats}.
            {isReduction && <span className="text-cyan-600"> {tr("workspace:panel.laReductionPrendraEffet")}</span>}
          </p>
          <Button data-testid="save-seats-btn" onClick={submit} disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}{tr("workspace:panel.valider")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const TransferDialog = ({ open, onOpenChange, workspaceId, admins, onDone }) => {
  const [target, setTarget] = useState(""); const [password, setPassword] = useState("");
  const [code, setCode] = useState(""); const [needCode, setNeedCode] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setTarget(""); setPassword(""); setCode(""); setNeedCode(false);
      api.post(`/workspaces/${workspaceId}/transfer-ownership/challenge`).then(({ data }) => setNeedCode(!!data.two_factor_required)).catch(() => {});
    }
  }, [open, workspaceId]);
  const submit = async () => {
    if (!target) { toast.error(tr("workspace:panel.choisissezLeNouvelAdministrateur")); return; }
    if (!password) { toast.error(tr("workspace:panel.motDePasseRequis")); return; }
    setBusy(true);
    try {
      await api.post(`/workspaces/${workspaceId}/transfer-ownership`, { new_owner_user_id: target, current_password: password, code: code || undefined });
      toast.success(tr("workspace:panel.proprieteTransferee")); onOpenChange(false); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="transfer-dialog">
        <DialogHeader><DialogTitle>{tr("workspace:panel.transfererLaPropriete")}</DialogTitle>
          <DialogDescription>{tr("workspace:panel.vousDeviendrezAdministrateurUn")}</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div><Label className="mb-1.5 block">{tr("workspace:panel.nouveauProprietaireAdministrateurActif")}</Label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger data-testid="transfer-new-owner-select" className="bg-slate-50 border-slate-200"><SelectValue placeholder={tr("workspace:panel.choisir")} /></SelectTrigger>
              <SelectContent>{admins.map((a) => <SelectItem key={a.user_id} value={a.user_id}>{a.email}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="mb-1.5 block">{tr("workspace:panel.votreMotDePasse")}</Label>
            <Input data-testid="transfer-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-slate-50 border-slate-200" /></div>
          {needCode && (
            <div><Label className="mb-1.5 block">{tr("workspace:panel.codeDeConfirmationEnvoye")}</Label>
              <Input data-testid="transfer-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" className="bg-slate-50 border-slate-200" /></div>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="transfer-confirm-btn" onClick={submit} disabled={busy} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowLeftRight className="w-4 h-4 mr-2" />}{tr("workspace:panel.confirmerLeTransfert")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
